import React, { ReactElement } from 'react';

import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useParams } from 'react-router';
import EOSLayout from 'src/components/EOS/EOSLayout';

import Authenticate from '../components/Auth/Authenticate';
import RequireAuth from '../components/Auth/RequireAuth';
import SuspenseLoadPage from '../components/Auth/SuspenceLoadPage';
import Base from '../components/Base';
import LandingPage from '../components/EOS/LandingPage';
import DefaultLayout from '../components/EOS/common/DefaultLayout';
import { EosTokenProvider } from '../components/EOS/services/EosTokenService';
import Layout from '../layout';
import ErrorComp from '../pages/Error';
import Forbidden from '../pages/Forbidden';
import Login from '../pages/Login';
import Logout from '../pages/Logout';
import { IFullState } from '../types/All.type';
import GenesisRoutes from './GenesisRoutes';
import eosRoutes from './eosRoutes';
import routepaths from './routepaths';
import Unauthorized from '../components/Auth/UnAuthorized';
import Loader from '../UI/Loader';
import { MESSAGE, rolePermissions } from '../constants/Permission';
import { selectEOSUser } from '../components/EOS/store/slices/eosUser';
import { getEnv } from 'src/utils/env.vite';
import { Flexbox } from '@sede-enterprise/shell-ds-react-framework';
import NewGenesisLayout from '../components/EUMarkets/NewGenesisLayout';
import HealthCheck from '../components/EUMarkets/Dashboard/HealthCheck';
import Messaging from '../components/EUMarkets/Messaging';
import Inbox from '../components/EUMarkets/Messaging/Inbox';
import ExternalInbox from '../components/EUMarkets/Messaging/ExternalInbox';
import Positions from '../components/EUMarkets/Positions';
import MarketPositionNew from '../components/EUMarkets/Positions/MarketPositionNew';
import Balances from '../components/EUMarkets/Positions/Balances';
import ContractViewNew from '../components/EUMarkets/Positions/ContractViewNew';
import SystemAdmin from '../components/EUMarkets/SystemAdmin';

interface EOSRouteGuardProps {
  permission?: string;
  children: React.ReactNode;
}

const NewGenesisSystemAdminRedirect: React.FC = () => {
  const { tabName = 'markets' } = useParams();
  return <Navigate to={`/new-genesis/system-admin/${tabName}`} replace />;
};

const EOSRouteGuard: React.FC<EOSRouteGuardProps> = ({ permission, children }) => {
  const { permissions, isFetching } = useSelector(selectEOSUser);

  if (isFetching) {
    return (
      <Flexbox justifyContent='center' alignItems='center' style={{ height: '100vh' }}>
        <Loader />
      </Flexbox>
    );
  }

  if (permission && !permissions.includes(permission)) {
    return <Unauthorized message={MESSAGE.unAuthorized} backOption />;
  }

  return <>{children}</>;
};

function AppRouter(): ReactElement {
  const userState = useSelector((state: IFullState) => state.genesisUserRolePermission);
  const isUserAuthorized = userState.isAuthorized;
  // Preprod serves only Shell Energy Europe, so skip tile selection.
  const isPreprod = getEnv('VITE_APP_ENVIRONMENT') === 'Preprod';
  const { isEosUser, permissions } = userState;
  const showFieldNomsRoute = permissions.includes(rolePermissions.listUkNomination);
  const showSwingContractMappingRoute = permissions.includes(rolePermissions.listContractMapping);
  const showCoastKeyMapperRoute = permissions.includes(rolePermissions.listBeachSwaps);
  const isFieldNomsRoute = (path: string) => path.startsWith('/field-noms');
  return (
    <Routes>
      <Route path={routepaths.login} element={<Login />} />
      {!isUserAuthorized && <Route path={routepaths.base} element={<Base />} />}
      <Route path={routepaths.authenticate} element={<Authenticate />} />

      <Route path={routepaths.logout} element={<Logout messageKey='M1' />} />
      <Route path={routepaths.unauthorized} element={<Logout messageKey='M2' />} />
      <Route path={routepaths.blockedAddress} element={<Logout messageKey='M3' />} />
      <Route path={routepaths.authError} element={<Logout messageKey='M4' />} />
      <Route path={routepaths.forbiddenAccess} element={<Logout messageKey='M5' />} />
      <Route path={routepaths.notAuthorized} element={<Logout messageKey='M6' />} />

      <Route element={<RequireAuth />}>
        {
          <Route element={<DefaultLayout />}>
            <Route
              path={routepaths.landingPage}
              element={
                isPreprod ? (
                  <Navigate to={routepaths.newGenesisDashboardHealthCheck} replace />
                ) : (
                  <LandingPage />
                )
              }
            />
          </Route>
        }

        <Route element={<Layout />}>
          {GenesisRoutes.filter(({ path }) => {
            const isFieldNoms = isFieldNomsRoute(path);
            const isCoastMapper = path === routepaths.coastKeyMapper;
            const isSwingMapping = path === routepaths.swingContractMapping;
            return (
              (!isFieldNoms || showFieldNomsRoute) &&
              (!isCoastMapper || showCoastKeyMapperRoute) &&
              (!isSwingMapping || showSwingContractMappingRoute)
            );
          }).map(({ path, element }) => (
            <Route key={`genesis_route_${path}`} path={path} element={element} />
          ))}
          <Route path={routepaths.error} element={<ErrorComp />} />
        </Route>

        {/* EOS - Only available when user has EOS access */}
        {isEosUser && (
          <Route
            path='/eos/*'
            element={
              <SuspenseLoadPage>
                <EosTokenProvider>
                  <EOSLayout />
                </EosTokenProvider>
              </SuspenseLoadPage>
            }
          >
            {eosRoutes.map(({ path, component: Component, fallback, permission }) => (
              <Route
                key={path}
                path={path}
                element={
                  <SuspenseLoadPage fallback={fallback}>
                    <EOSRouteGuard permission={permission}>
                      <Component />
                    </EOSRouteGuard>
                  </SuspenseLoadPage>
                }
              />
            ))}
          </Route>
        )}

        {/* New Genesis routes */}
        <Route path='/new-genesis' element={<NewGenesisLayout />}>
          <Route path='dashboard' element={<HealthCheck />} />
          <Route path='messaging' element={<Messaging />}>
            <Route path='inbox' element={<Inbox />} />
            <Route path='external-inbox' element={<ExternalInbox />} />
            <Route index element={<Navigate to='inbox' replace />} />
          </Route>
          <Route path='positions' element={<Positions />}>
            <Route path='balances' element={<Balances />} />
            <Route path='market-position' element={<MarketPositionNew />} />
            <Route path='contract-view' element={<ContractViewNew />} />
            <Route index element={<Navigate to='balances' replace />} />
          </Route>
          <Route path='system-admin/:tabName' element={<SystemAdmin />} />
          <Route index element={<Navigate to='dashboard' replace />} />
        </Route>

        <Route path='*' element={<span>404</span>} />
        <Route path={routepaths.forbidden} element={<Forbidden />} />
      </Route>
    </Routes>
  );
}

export default AppRouter;
